import { useEffect, useState } from "react";
import { Loader2, History, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { getContract, STATUS_META, transition } from "../../services/b2bContracts";

/**
 * ContractDetailDialog — read-only inspector with the full audit
 * timeline. Used by both admin and city_manager via the "View" button.
 * Customer-facing rendering lives in a separate page (Phase 2).
 */
export default function ContractDetailDialog({ contractId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contractId) { setDoc(null); return; }
    (async () => {
      setLoading(true);
      try { setDoc(await getContract(contractId)); }
      catch { toast.error("Could not load contract"); }
      finally { setLoading(false); }
    })();
  }, [contractId]);

  const doClone = async () => {
    try {
      const res = await transition(contractId, "clone");
      toast.success(`Cloned as new draft ${res.id.slice(-6)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };

  if (!contractId) return null;
  const meta = doc ? STATUS_META[doc.status] : null;

  return (
    <Dialog open={!!contractId} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#FF6B00]" />
            Contract details
          </DialogTitle>
        </DialogHeader>

        {loading || !doc ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" /></div>
        ) : (
          <div className="space-y-4">
            <header className="flex items-start justify-between gap-3">
              <div>
                <p className="text-lg font-bold text-slate-900">{doc.company_name}</p>
                <p className="text-xs text-slate-500">{doc.legal_name || "—"} · {doc.gstin || "no GSTIN"} · v{doc.version}</p>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${meta.color}`}>
                {meta.label}
              </span>
            </header>

            <Pair label="Validity" value={`${fmt(doc.start_date)} → ${fmt(doc.end_date)}`} />
            <Pair label="Scope" value={`${doc.origin_city || "any city"} · ${doc.origin_hub_id || "any hub"}`} />
            <Pair label="Users" value={doc.user_ids?.length ? doc.user_ids.join(", ") : "—"} />

            <Block title="Rate cards">
              {doc.rate_cards?.length ? doc.rate_cards.map((rc, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-3 mb-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                    {rc.scope} · {rc.service_type}{rc.scope === "hub_pair" ? ` · ${rc.origin_hub_id} → ${rc.destination_hub_id}` : ""}
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-500 font-semibold uppercase tracking-wider pb-1">
                    <div>Up to (kg)</div><div>Base (INR)</div><div>Per extra kg</div>
                  </div>
                  {rc.slabs?.map((s, j) => (
                    <div key={j} className="grid grid-cols-3 gap-2 text-sm py-0.5">
                      <div>{s.up_to_kg}</div>
                      <div>INR {s.base_inr}</div>
                      <div>{s.per_extra_kg_inr}</div>
                    </div>
                  ))}
                </div>
              )) : <p className="text-xs text-slate-500">No rate cards yet.</p>}
            </Block>

            <Block title="Ancillary">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <Stat k="Pickup" v={`INR ${doc.pickup_charge}`} />
                <Stat k="Packing" v={`INR ${doc.packing_charge}`} />
                <Stat k="COD flat" v={`INR ${doc.cod_fee_flat}`} />
                <Stat k="COD %" v={`${doc.cod_fee_pct}%`} />
                <Stat k="RTO %" v={`${doc.rto_fee_pct}%`} />
                <Stat k="Fuel %" v={`${doc.fuel_surcharge_pct}%`} />
                <Stat k="GST %" v={`${doc.gst_pct}%`} />
                <Stat k="Volume disc" v={`${doc.volume_discount_pct}%`} />
                <Stat k="Special disc" v={`${doc.special_discount_pct}%`} />
              </div>
            </Block>

            {doc.notes && <Pair label="Notes" value={doc.notes} />}
            {doc.rejection_reason && (
              <div className="bg-rose-50 border border-rose-100 rounded-xl p-3">
                <p className="text-xs font-bold text-rose-700 uppercase tracking-wider">Rejection reason</p>
                <p className="text-sm text-rose-700 mt-1">{doc.rejection_reason}</p>
              </div>
            )}

            <Block title={<span className="flex items-center gap-1"><History className="h-3.5 w-3.5" /> Audit timeline</span>}>
              <ol className="border-l border-slate-200 pl-4 space-y-3" data-testid="contract-history">
                {(doc.history || []).slice().reverse().map((h, i) => (
                  <li key={i} className="relative">
                    <span className="absolute -left-[18px] top-1.5 h-2 w-2 rounded-full bg-[#FF6B00]" />
                    <p className="text-sm font-semibold text-slate-800">
                      {prettifyAction(h.action)} {h.by_role && <span className="text-[10px] font-medium text-slate-400">by {h.by_role}</span>}
                    </p>
                    <p className="text-[11px] text-slate-400">{new Date(h.timestamp).toLocaleString()} · {h.by_email || ""}</p>
                    {(h.previous_status || h.next_status) && (
                      <p className="text-[11px] text-slate-500 mt-0.5">{h.previous_status || "?"} → {h.next_status || "?"}</p>
                    )}
                    {h.comment && <p className="text-xs text-slate-600 mt-1">"{h.comment}"</p>}
                    {h.reason && <p className="text-xs text-slate-600 mt-0.5">Reason: {h.reason}</p>}
                    {h.changes && Object.keys(h.changes).length > 0 && (
                      <div className="text-[11px] text-slate-500 mt-1 bg-slate-50 border border-slate-100 rounded p-1.5">
                        {Object.entries(h.changes).map(([k, [a, b]]) => (
                          <div key={k}><b>{k}</b>: {JSON.stringify(a)} → {JSON.stringify(b)}</div>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            </Block>

            <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
              <Button variant="outline" size="sm" onClick={doClone} data-testid="contract-detail-clone">
                <Copy className="h-3.5 w-3.5 mr-1" /> Clone as new draft
              </Button>
              <Button onClick={() => onClose?.()} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function fmt(d) { return d ? new Date(d).toLocaleDateString() : "—"; }
function Pair({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm text-slate-800">{value}</span>
    </div>
  );
}
function Block({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{title}</h4>
      {children}
    </div>
  );
}
function Stat({ k, v }) {
  return (
    <div className="bg-slate-50 border border-slate-100 rounded-lg p-2">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-sm font-semibold text-slate-800">{v}</div>
    </div>
  );
}
function prettifyAction(a) {
  return String(a || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
